import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, FileText, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formsApi } from '../../services/api';
import { showSuccess, showError } from '../../utils/toast';
import LoadingCard from '../../components/LoadingCard';
import { useForms } from './hooks/useForms';
import ShareKeyModal from './components/ShareKeyModal';

const FormsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [shareKeyModalOpen, setShareKeyModalOpen] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState<number | null>(null);
  
  const { forms, loading, refetch } = useForms({
    search: search || undefined,
  });

  const openShareKeyModal = (formId: number) => {
    setSelectedFormId(formId);
    setShareKeyModalOpen(true);
  };

  const closeShareKeyModal = () => {
    setShareKeyModalOpen(false);
    setSelectedFormId(null);
  };

  const handleDelete = async (id: number, title: string) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;

    try {
      await formsApi.delete(id);
      showSuccess('Form deleted successfully');
      refetch();
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to delete form');
    }
  };

  if (loading) {
    return <LoadingCard title="Dynamic Forms" message="Loading forms..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-2xl font-semibold theme-text-primary">
            Dynamic Forms
          </h1>
          <p className="theme-text-secondary mt-1">Manage your lead collection forms</p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/form/builder')}
            className="flex items-center justify-center gap-1.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 py-1.5 rounded-lg hover:shadow-lg transition-shadow text-sm"
          >
            <Plus className="w-5 h-5" />
            Create New Form
          </button>
        </div>
      </motion.div>

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="theme-card rounded-xl shadow-sm p-4"
      >
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium theme-text-primary">Search:</span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search forms..."
            className="w-full pl-4 pr-4 py-2 theme-border rounded-lg border border-white  theme-border rounded-lg p-2  focus:outline-none 
         focus:border-blue-500 
  focus:ring-0  theme-bg-primary theme-text-primary  border border-white theme-bg-primary theme-text-primary"
          />
        </div>
      </motion.div>

      {/* Forms Grid */}
      {forms.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="theme-card rounded-xl shadow-sm p-12 text-center"
        >
          <div className="w-20 h-20 theme-bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
            <Plus className="w-10 h-10 theme-text-tertiary" />
          </div>
          <h3 className="text-xl font-semibold theme-text-primary mb-2">No forms yet</h3>
          <p className="theme-text-secondary mb-6">Create your first form to start collecting leads</p>
          <button
            onClick={() => navigate('/form/builder')}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all inline-flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Create Form
          </button>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {forms.map((form, index) => (
            <motion.div
              key={form.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="theme-card rounded-xl shadow-sm hover:shadow-md transition-all p-6 theme-border"
            >
              {/* Actions */}
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => form.id && navigate(`/form/${form.id}/submissions`)}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <FileText className="w-4 h-4" />
                  Entries
                </button>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => form.id && openShareKeyModal(form.id)}
                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Generate Share Key"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => navigate(`/form/builder/${form.id}`)}
                    className="p-2 theme-text-secondary hover:theme-bg-secondary rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => form.id && handleDelete(form.id, form.title)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Form Info */}
              <h3 className="text-lg font-semibold theme-text-primary mb-2 line-clamp-1">
                {form.title}
              </h3>
              {form.description && (
                <p className="text-sm theme-text-secondary mb-4 line-clamp-2">{form.description}</p>
              )}

              {/* Stats */}
              <div className="flex items-center gap-4 mb-4 text-sm theme-text-secondary">
                <div className="flex items-center gap-1">
                  <span className="font-medium">{form.fields?.length || 0}</span>
                  <span>fields</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-4 theme-border-t">
                <button
                  onClick={() => navigate(`/form/builder/${form.id}`)}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                >
                  <Edit className="w-4 h-4" />
                  Edit Form
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Share Key Modal */}
      {selectedFormId && (
        <ShareKeyModal
          isOpen={shareKeyModalOpen}
          onClose={closeShareKeyModal}
          formId={selectedFormId}
        />
      )}
    </div>
  );
};

export default FormsListPage;
